import { Box, Typography, Stack, StackProps } from '@mui/material';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip, TooltipLabel } from '../InfoTooltip';
import HighlightedText from 'components/GlobalSearch/HighlightText';

export type AxisValueType = 'number' | 'currency';

export interface AreaTrendPoint {
  YearMonth: string;
  Count: number;
  [key: string]: string | number | null | undefined;
}

export type MonthlyTrendApiData = Record<string, AreaTrendPoint[]>;

export interface AreaTrendSeriesConfig {
  dataKey: string;
  name: string;
  color: string;
  yAxisIndex?: 0 | 1;
  areaOpacity?: number;
  showSymbol?: boolean;
  showLabel?: boolean;
  areaColor?: any;
}

type TooltipParam = {
  axisValueLabel?: string;
  seriesName?: string;
  value?: number | string;
  marker?: string;
};

interface AreaTrendComparisonChartProps {
  title: string;
  subTitle?: string;
  isLoading?: boolean;

  options?: object;

  data?: MonthlyTrendApiData | null;
  seriesConfig?: AreaTrendSeriesConfig[];

  monthKey?: string;
  valueKey?: string;

  valueType?: AxisValueType;
  rightValueType?: AxisValueType;

  leftYAxisName?: string;
  rightYAxisName?: string;
  xAxisLabel?: string;

  showRightYAxis?: boolean;

  showTrendLine?: boolean;
  trendValue?: number;
  trendName?: string;
  trendColor?: string;
  trendYAxisIndex?: 0 | 1;

  height?: number | string;
  showLegend?: boolean;
  sortDirection?: 'asc' | 'desc';

  hasTooltip?: boolean;
  tooltipLabel?: TooltipLabel;

  minTitleHeight?: `${number}px`;
  hasFilter?: boolean;
  children?: React.ReactNode;
  childrenContainerProps?: StackProps;
  containerProps?: StackProps;
}

const MONTH_LABELS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC'
];

const formatMonthLabel = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  const monthIndex = Number(month) - 1;

  if (monthIndex >= 0 && monthIndex <= 11) {
    return `${MONTH_LABELS[monthIndex]} ${year}`;
  }

  return yearMonth;
};

const getMonthSortValue = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-').map(Number);

  if (!year || !month) return 0;

  return year * 12 + month;
};

const getSafeNumber = (value: unknown) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatYAxisValue = (value: number, valueType: AxisValueType) => {
  const prefix = valueType === 'currency' ? '$' : '';

  if (value >= 1_000_000) return `${prefix}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${prefix}${(value / 1_000).toFixed(1)}K`;

  return `${prefix}${value}`;
};

const getNiceMax = (values: number[]) => {
  const maxValue = Math.max(...values, 0);

  if (maxValue <= 0) return 10;
  if (maxValue <= 10) return 10;
  if (maxValue <= 100) return Math.ceil(maxValue / 10) * 10;

  return Math.ceil(maxValue / 100) * 100;
};

const getAverageValue = (values: number[]) => {
  if (!values.length) return 0;

  const total = values.reduce((sum, value) => sum + value, 0);
  return Math.round(total / values.length);
};

const AreaTrendComparisonChart = ({
  title,
  subTitle,
  isLoading = false,
  options,
  data,
  seriesConfig = [],
  monthKey = 'YearMonth',
  valueKey = 'Count',
  valueType = 'number',
  rightValueType = 'number',
  leftYAxisName,
  rightYAxisName,
  xAxisLabel = '',
  showRightYAxis = false,
  showTrendLine = false,
  trendValue,
  trendName = 'Trend',
  trendColor = '#FF8A1C',
  trendYAxisIndex = 0,
  height = 350,
  showLegend = true,
  sortDirection = 'asc',
  hasTooltip,
  tooltipLabel,
  minTitleHeight = '38px',
  hasFilter,
  children,
  childrenContainerProps,
  containerProps
}: AreaTrendComparisonChartProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartRef = useRef<ReactECharts>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance()?.resize();
    });

    const instance = chartRef.current?.getEchartsInstance();

    if (instance) {
      resizeObserver.observe(instance.getDom());
    }

    return () => resizeObserver.disconnect();
  }, []);

  const generatedOption = useMemo(() => {
    if (!data || !seriesConfig.length) {
      return {
        backgroundColor: colors?.widget?.background?.chart
      };
    }

    const allMonths = Array.from(
      new Set(
        seriesConfig.flatMap((series) =>
          (data[series.dataKey] ?? [])
            .map((item) => String(item[monthKey] ?? ''))
            .filter(Boolean)
        )
      )
    ).sort((a, b) => {
      const result = getMonthSortValue(a) - getMonthSortValue(b);
      return sortDirection === 'asc' ? result : -result;
    });

    const xAxisLabels = allMonths.map(formatMonthLabel);

    const leftYAxisColor =
      seriesConfig.find((series) => (series.yAxisIndex ?? 0) === 0)?.color ??
      colors?.widget?.chart?.labelColorSecondary;

    const rightYAxisColor =
      seriesConfig.find((series) => series.yAxisIndex === 1)?.color ??
      colors?.widget?.chart?.labelColorSecondary;

    const leftAxisValues: number[] = [];
    const rightAxisValues: number[] = [];

    const seriesValueTypeMap = new Map<string, AxisValueType>();

    const chartSeries = seriesConfig.map((series) => {
      const source = data[series.dataKey] ?? [];

      const valueMap = new Map(
        source.map((item) => [
          String(item[monthKey] ?? ''),
          getSafeNumber(item[valueKey])
        ])
      );

      const seriesData = allMonths.map((month) => valueMap.get(month) ?? 0);

      if (series.yAxisIndex === 1) {
        rightAxisValues.push(...seriesData);
        seriesValueTypeMap.set(series.name, rightValueType);
      } else {
        leftAxisValues.push(...seriesData);
        seriesValueTypeMap.set(series.name, valueType);
      }

      return {
        name: series.name,
        type: 'line',
        smooth: false,
        symbol: 'circle',
        symbolSize: 9,
        showSymbol: series.showSymbol ?? true,
        yAxisIndex: series.yAxisIndex ?? 0,
        data: seriesData,
        label: {
          show: series.showLabel ?? false,
          position: 'top',
          color: series.color,
          fontSize: 11
        },
        itemStyle: {
          color: series.color
        },
        lineStyle: {
          color: series.color,
          width: 2,
          opacity: 0.2
        },
        areaStyle: {
          opacity: series.areaOpacity ?? 0.25,
          color: series.areaColor ?? series.color
        }
      };
    });

    if (showTrendLine && allMonths.length) {
      const trendLineValue =
        typeof trendValue === 'number' ? trendValue : getAverageValue(leftAxisValues);

      if (trendYAxisIndex === 1) {
        rightAxisValues.push(trendLineValue);
        seriesValueTypeMap.set(trendName, rightValueType);
      } else {
        leftAxisValues.push(trendLineValue);
        seriesValueTypeMap.set(trendName, valueType);
      }

      chartSeries.push({
        name: trendName,
        type: 'line',
        smooth: false,
        symbol: 'none',
        symbolSize: 0,
        showSymbol: false,
        yAxisIndex: trendYAxisIndex,
        data: allMonths.map(() => trendLineValue),
        label: {
          show: false
        },
        itemStyle: {
          color: trendColor
        },
        lineStyle: {
          color: trendColor,
          width: 2
        }
      } as never);
    }

    const hasRightYAxis =
      showRightYAxis || seriesConfig.some((series) => series.yAxisIndex === 1);

    return {
      backgroundColor: colors?.widget?.background?.chart,
      textStyle: {
        color: colors?.widget?.chart?.labelColorSecondary
      },

      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: (params: TooltipParam[]) => {
          const date = params?.[0]?.axisValueLabel ?? '';
          let tooltipContent = `<div>${date}</div>`;

          params.forEach((item) => {
            const val = Number(item?.value) || 0;
            const currentValueType =
              seriesValueTypeMap.get(item.seriesName ?? '') ?? valueType;
            const formatted = formatYAxisValue(val, currentValueType);

            tooltipContent += `<div>${item.marker ?? ''}<strong>${item.seriesName}</strong>: <strong>${formatted}</strong></div>`;
          });

          return tooltipContent;
        }
      },

      legend: {
        show: showLegend,
        orient: 'horizontal',
        left: 0,
        bottom: 0,
        itemGap: 24,
        icon: 'rect',
        itemWidth: 14,
        itemHeight: 8,
        textStyle: {
          color: colors?.widget?.chart?.legendTextColor,
          fontSize: 12
        }
      },

      grid: {
        top: 24,
        left: '5%',
        right: hasRightYAxis ? '8%' : '5%',
        bottom: showLegend ? '18%' : '10%',
        containLabel: true
      },

      xAxis: {
        type: 'category',
        data: xAxisLabels,
        name: xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        boundaryGap: false,
        nameTextStyle: {
          color: colors?.widget?.chart?.labelColorSecondary
        },
        axisLabel: {
          color: colors?.widget?.chart?.labelColorSecondary
        },
        axisTick: {
          show: false
        },
        axisLine: {
          lineStyle: {
            color: colors?.widget?.chart?.labelColorSecondary
          }
        }
      },

      yAxis: [
        {
          type: 'value',
          name: leftYAxisName,
          min: 0,
          max: getNiceMax(leftAxisValues),
          nameLocation: 'middle',
          nameGap: 44,
          nameTextStyle: {
            color: colors?.widget?.chart?.labelColorSecondary
          },
          axisLabel: {
            color: colors?.widget?.chart?.labelColorSecondary,
            formatter: (value: number) => formatYAxisValue(value, valueType)
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: leftYAxisColor
            }
          },
          axisTick: {
            lineStyle: {
              color: leftYAxisColor
            }
          },
          splitLine: false
        },
        {
          type: 'value',
          show: hasRightYAxis,
          name: rightYAxisName,
          min: 0,
          max: getNiceMax(rightAxisValues),
          nameLocation: 'middle',
          nameGap: 48,
          nameTextStyle: {
            color: colors?.widget?.chart?.labelColorSecondary
          },
          axisLabel: {
            color: colors?.widget?.chart?.labelColorSecondary,
            formatter: (value: number) => formatYAxisValue(value, rightValueType)
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: rightYAxisColor
            }
          },
          axisTick: {
            lineStyle: {
              color: rightYAxisColor
            }
          },
          splitLine: false
        }
      ],

      series: chartSeries
    };
  }, [
    data,
    seriesConfig,
    monthKey,
    valueKey,
    valueType,
    rightValueType,
    leftYAxisName,
    rightYAxisName,
    xAxisLabel,
    showRightYAxis,
    showTrendLine,
    trendValue,
    trendName,
    trendColor,
    trendYAxisIndex,
    showLegend,
    sortDirection,
    colors
  ]);

  const finalOption = options ?? generatedOption;
  const chartHeight = typeof height === 'number' ? `${height}px` : height;

  return (
    <WidgetWraper>
      <Box sx={{ backgroundColor: colors.widget.background.card }} {...containerProps}>
        <Stack direction="column" gap={2} sx={{ minHeight: minTitleHeight }}>
          <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="widgetTitleNew" sx={{ color: colors.widget.text.title }}>
                <HighlightedText text={title} />
              </Typography>
            {hasTooltip && tooltipLabel && <InfoTooltip label={tooltipLabel} />}
          </Stack>

          {subTitle && (
            <Typography variant="bodyText" sx={{ color: colors.widget.text.title }}>
              {subTitle}
            </Typography>
          )}
        </Stack>

        {hasFilter && (
          <Stack direction="row" justifyContent="flex-end" mt={1} {...childrenContainerProps}>
            {children}
          </Stack>
        )}

        <Box height={chartHeight} mt={2}>
          {!isLoading ? (
            <ReactECharts
              ref={chartRef}
              option={finalOption}
              notMerge
              lazyUpdate
              style={{ height: '100%', width: '100%' }}
            />
          ) : (
            <LoaderComponent pt={10} />
          )}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default AreaTrendComparisonChart;